import { type SyntheticEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type DeadlineDraft,
  type DeadlineErrorCode,
  type DeadlinePayload,
  draftFromDeadline,
  rangeKind,
  rangeToPayload,
  validateRange,
} from '@/shared/lib/deadline-draft';

import { Button } from './Button';
import { DeadlineFields } from './DeadlineFields';
import styles from './DeadlineForm.module.css';

interface Props {
  /** Current deadline (server shape) or `null` for a new one. */
  initial: Parameters<typeof draftFromDeadline>[0];
  /** Profile timezone the values are interpreted in (shown, never sent). */
  timezone: string | null;
  pending: boolean;
  /** Rendered under the fields, e.g. a server error. */
  error?: React.ReactNode;
  onSave: (payload: DeadlinePayload) => void;
  /** Present only when a deadline exists. */
  onRemove?: () => void;
  onCancel: () => void;
}

/**
 * Start / End form with Save for the places that edit dates in a small dialog (a tile's quick
 * edit, Quick Create). Pure UI: the caller saves. Same rules as everywhere (`rangeKind`): End only
 * = a task with a deadline, Start + End = an event, Start only = an error, both empty = no date
 * (saving then removes the existing date, if any).
 */
export function DeadlineForm({
  initial,
  timezone,
  pending,
  error,
  onSave,
  onRemove,
  onCancel,
}: Props) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<DeadlineDraft>(() => draftFromDeadline(initial));
  const [errors, setErrors] = useState<DeadlineErrorCode[]>([]);

  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    const found = validateRange(draft);
    setErrors(found);
    if (found.length > 0) return;
    const payload = rangeToPayload(draft);
    if (payload) onSave(payload);
    else if (onRemove) onRemove();
    else onCancel();
  };

  return (
    <form
      className={styles.form}
      noValidate
      onSubmit={submit}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          // Close only this form, not a surrounding dialog.
          event.preventDefault();
          event.stopPropagation();
          onCancel();
        }
      }}
    >
      <DeadlineFields
        value={draft}
        errors={errors}
        timezone={timezone}
        onChange={(next) => {
          setDraft(next);
          setErrors([]);
        }}
      />
      {error}
      <div className={styles.actions}>
        <Button type="submit" variant="primary" size="sm" disabled={pending}>
          {t('deadline.save')}
        </Button>
        {onRemove && rangeKind(draft) !== 'none' && (
          <Button size="sm" variant="ghost" disabled={pending} onClick={onRemove}>
            {t('deadline.remove')}
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  );
}
